/**
 * Payout accounts + history screen.
 *
 * Shows the list of payout accounts the host has added, the default-account
 * marker, and a scrollable history of the last 50 payouts with status chips.
 *
 * With no account AND no payout the two sections are replaced by a single
 * first-run setup (see `FirstPayoutSetup`): a history section cannot precede
 * the account that produces it.
 */
import { useCallback } from 'react'
import { View, Text, RefreshControl } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import {
  Landmark,
  CheckCircle,
  Clock,
  XCircle,
  ChevronRight,
  Star,
  Receipt,
} from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { Pressable } from '@/components/ui/Pressable'
import { Skeleton } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import {
  usePayoutAccounts,
  usePayoutHistory,
  useSetDefaultPayoutAccount,
} from '@/lib/hooks/use-payouts'
import { useSemanticColors, withAlpha, SPACING, RADIUS } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import type { PayoutAccount, Payout } from '@/lib/types/payout.types'
import { formatCurrency } from '@/lib/utils/format'
import { formatBookingDate } from '@/lib/utils/dates'

export default function PayoutsScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const {
    data: accounts,
    isLoading: accountsLoading,
    isError: accountsError,
    refetch: refetchAccounts,
  } = usePayoutAccounts()
  const {
    data: history,
    isLoading: historyLoading,
    isError: historyError,
    refetch: refetchHistory,
  } = usePayoutHistory()
  const { mutateAsync: setDefault, isPending: settingDefault } = useSetDefaultPayoutAccount()
  const enter = useEntrance(!accountsLoading)

  const refetching = accountsLoading || historyLoading
  const handleRefresh = useCallback(async () => {
    await Promise.all([refetchAccounts(), refetchHistory()])
  }, [refetchAccounts, refetchHistory])

  // Neither an account nor a payout: this host has never been paid, so the
  // screen swaps its two-section list for the first-run setup below.
  const accountsEmpty = !accountsLoading && !accountsError && (accounts?.length ?? 0) === 0
  const historyEmpty = !historyLoading && !historyError && (history?.length ?? 0) === 0
  const firstRun = accountsEmpty && historyEmpty

  const goToOnboarding = useCallback(() => {
    router.push('/hosting/payouts/onboarding' as never)
  }, [router])

  const handleSetDefault = useCallback(
    async (accountId: string) => {
      try {
        await setDefault(accountId)
        toast.success(t('payouts.default_set'))
      } catch (err) {
        if (__DEV__) console.error('[Payouts] setDefault', err)
        toast.error(errorMessage(err, t('errors.generic')))
        captureError(err, { screen: 'payouts', action: 'set_default' }, { accountId })
      }
    },
    [setDefault, t],
  )

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('payouts.title')} />

      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: SPACING.lg,
          paddingBottom: SPACING.xl * 2,
          // Section → section is deliberately larger than header → content
          // (SPACING.md inside each section below). It used to be the other way
          // round, so "Añadir cuenta" read as the first item under HISTORIAL.
          gap: SPACING.xl,
          ...(firstRun ? { flexGrow: 1, justifyContent: 'center' } : null),
        }}
        refreshControl={
          <RefreshControl
            refreshing={refetching}
            onRefresh={handleRefresh}
            tintColor={COLORS.coral.DEFAULT}
          />
        }
      >
        {firstRun ? (
          <FirstPayoutSetup onAddAccount={goToOnboarding} />
        ) : (
          <>
            {/* ── Accounts section ───────────────────────────────────── */}
            <View style={{ gap: SPACING.md }}>
              <SectionLabel title={t('payouts.accounts_section')} />

              {accountsLoading ? (
                <View style={{ gap: SPACING.sm }}>
                  <Skeleton width="100%" height={72} borderRadius={RADIUS.lg} />
                  <Skeleton width="100%" height={72} borderRadius={RADIUS.lg} />
                </View>
              ) : accountsError ? (
                <ErrorCard message={t('errors.generic')} onRetry={refetchAccounts} />
              ) : !accounts || accounts.length === 0 ? (
                <EmptyState
                  variant="bloom"
                  compact
                  align="start"
                  illustration={
                    <Landmark size={26} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />
                  }
                  title={t('payouts.no_accounts')}
                  description={t('payouts.no_accounts_desc')}
                />
              ) : (
                accounts.map((acc, i) => (
                  <Animated.View key={acc.id} entering={enter(i)} collapsable={false}>
                    <AccountCard
                      account={acc}
                      onSetDefault={() => handleSetDefault(acc.id)}
                      disabled={settingDefault}
                    />
                  </Animated.View>
                ))
              )}

              <Button
                title={t('payouts.add_account')}
                onPress={goToOnboarding}
                variant={!accounts || accounts.length === 0 ? 'primary' : 'secondary'}
                size="lg"
                testID="payouts.add-account"
              />
            </View>

            {/* ── History section ───────────────────────────────────── */}
            <View style={{ gap: SPACING.md }}>
              <SectionLabel title={t('payouts.history_section')} />

              {historyLoading ? (
                <View style={{ gap: SPACING.sm }}>
                  <Skeleton width="100%" height={56} borderRadius={RADIUS.lg} />
                  <Skeleton width="100%" height={56} borderRadius={RADIUS.lg} />
                  <Skeleton width="100%" height={56} borderRadius={RADIUS.lg} />
                </View>
              ) : historyError ? (
                <ErrorCard message={t('errors.generic')} onRetry={refetchHistory} />
              ) : !history || history.length === 0 ? (
                // Reached only when an account exists: the section is genuinely
                // empty rather than premature, so the tile explains why (no
                // completed trip yet) instead of offering an action it cannot
                // have. With zero accounts the screen renders FirstPayoutSetup
                // instead and this section does not exist at all.
                <EmptyState
                  variant="bloom"
                  compact
                  align="start"
                  illustration={
                    <Receipt size={26} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />
                  }
                  title={t('payouts.no_history')}
                  description={t('earnings.no_earnings_desc')}
                />
              ) : (
                history.map((p) => <PayoutRow key={p.id} payout={p} />)
              )}
            </View>
          </>
        )}
      </ScrollView>
    </ScreenContainer>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────

/** TUS CUENTAS / HISTORIAL / CÓMO FUNCIONA — one definition, three call sites. */
function SectionLabel({ title }: { title: string }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <Text
      style={{
        fontSize: 12,
        fontFamily: FONTS.body,
        color: colors.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
      }}
    >
      {title}
    </Text>
  )
}

/**
 * First run — no account, and therefore no payout that could ever have
 * happened.
 *
 * The screen used to render both sections anyway: TUS CUENTAS with an empty
 * tile, and under it HISTORIAL with an empty tile that has no action and
 * cannot be given one. Measured on the light render, that left 202px of bare
 * background above the first line and 320px below the last — 37% of the
 * scrollable height — and the history block read as a half-built section
 * floating in a void.
 *
 * Padding was never the fix. A payout history is not *empty* before a bank
 * account exists, it is *premature*; the screen has exactly one job and now
 * does it once — what this is, the three steps it takes, and the button that
 * starts it. Both sections come back the instant either has something to show.
 */
function FirstPayoutSetup({ onAddAccount }: { onAddAccount: () => void }) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const steps = [
    { title: t('payouts.step_account'), description: t('payouts.step_account_desc') },
    { title: t('payouts.step_trip'), description: t('payouts.step_trip_desc') },
    { title: t('payouts.step_deposit'), description: t('payouts.step_deposit_desc') },
  ]

  return (
    // No gap on the outer stack. EmptyState carries SPACING['2xl'] of its own
    // padding, so any gap here is added to 48pt that is already there — with
    // SPACING.lg the measured hole between the title and the card was 151px
    // (75pt), wider than the 86pt margin above the whole block, and the title
    // read as orphaned from the card explaining it. The card and the button
    // keep their own gap below.
    <View>
      <EmptyState
        variant="bloom"
        illustration={<Landmark size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />}
        title={t('payouts.setup_title')}
      />

      <View
        style={{
          backgroundColor: colors.surface,
          borderRadius: RADIUS.lg,
          borderWidth: 1,
          borderColor: colors.border,
          padding: SPACING.md,
          gap: SPACING.md,
          marginBottom: SPACING.lg,
          ...WEB_SHADOWS.sm,
        }}
      >
        <SectionLabel title={t('payouts.how_it_works')} />
        {steps.map((step, index) => (
          <View
            key={step.title}
            style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.md }}
          >
            {/* The ordinal is textPrimary, not coral: three coral discs with
                three coral numerals directly above a coral CTA turns the
                explanation into four competing calls to action, and 13pt coral
                on its own tint is under 4.5:1 in dark. The tint stays on the
                disc, where 3:1 governs. */}
            <View
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: withAlpha(COLORS.coral.DEFAULT, isDark ? 0.2 : 0.12),
              }}
            >
              <Text style={{ fontSize: 13, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
                {index + 1}
              </Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontSize: 14, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
                {step.title}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  marginTop: SPACING.xs,
                }}
              >
                {step.description}
              </Text>
            </View>
          </View>
        ))}
      </View>

      <Button
        title={t('payouts.add_account')}
        onPress={onAddAccount}
        variant="primary"
        size="lg"
        testID="payouts.add-account"
      />
    </View>
  )
}

interface AccountCardProps {
  account: PayoutAccount
  onSetDefault: () => void
  disabled: boolean
}

function AccountCard({ account, onSetDefault, disabled }: AccountCardProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()

  return (
    <View
      style={{
        backgroundColor: colors.surface,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        gap: SPACING.sm,
        ...WEB_SHADOWS.sm,
        borderWidth: 1,
        borderColor: account.isDefault ? `${COLORS.coral.DEFAULT}66` : colors.border,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: RADIUS.md,
            backgroundColor: account.isDefault ? `${COLORS.coral.DEFAULT}10` : colors.surfaceSubtle,
            borderWidth: 1,
            borderColor: account.isDefault ? `${COLORS.coral.DEFAULT}33` : colors.border,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Landmark
            size={20}
            color={account.isDefault ? COLORS.coral.DEFAULT : colors.textSecondary}
            strokeWidth={1.8}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Text
            style={{
              fontSize: 15,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
            numberOfLines={1}
          >
            {account.bankName ?? t('payouts.unknown_bank')}
          </Text>
          <Text
            style={{
              fontSize: 12,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              marginTop: SPACING.xs,
            }}
          >
            {account.accountNumberMasked} · {account.accountHolderName ?? ''}
          </Text>
        </View>
        {account.isVerified ? (
          <CheckCircle size={18} color={colors.success} />
        ) : (
          <Clock size={18} color={colors.warning} />
        )}
      </View>

      {!account.isDefault && (
        <Pressable
          onPress={onSetDefault}
          disabled={disabled}
          accessibilityRole="button"
          accessibilityLabel={t('payouts.set_default')}
          testID={`payouts.set-default-${account.id}`}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: SPACING.xs,
            alignSelf: 'flex-start',
            paddingVertical: SPACING.xs,
          }}
        >
          <Star size={14} color={colors.textSecondary} />
          <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textSecondary }}>
            {t('payouts.set_default')}
          </Text>
        </Pressable>
      )}
      {account.isDefault && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
          <Star size={14} color={COLORS.coral.DEFAULT} fill={COLORS.coral.DEFAULT} />
          <Text style={{ fontSize: 12, fontFamily: FONTS.bodyBold, color: colors.textAccent }}>
            {t('payouts.default_label')}
          </Text>
        </View>
      )}
    </View>
  )
}

function PayoutRow({ payout }: { payout: Payout }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()

  const statusIcon =
    payout.status === 'completed' ? (
      <CheckCircle size={18} color={colors.success} />
    ) : payout.status === 'failed' || payout.status === 'reversed' ? (
      <XCircle size={18} color={colors.textError} />
    ) : (
      <Clock size={18} color={colors.warning} />
    )

  const date = formatBookingDate(payout.initiatedAt, 'es', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        backgroundColor: colors.surface,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        ...WEB_SHADOWS.sm,
      }}
    >
      {statusIcon}
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 14, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
          {formatCurrency(payout.amountCents)} {payout.currency}
        </Text>
        <Text
          style={{
            fontSize: 12,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            marginTop: SPACING.xs,
          }}
        >
          {date} · {t(`payouts.status_${payout.status}` as const)}
        </Text>
      </View>
      <ChevronRight size={16} color={colors.textMuted} />
    </View>
  )
}

function ErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        backgroundColor: `${colors.error}14`,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
      }}
    >
      <XCircle size={20} color={colors.textError} />
      <Text style={{ flex: 1, fontSize: 13, fontFamily: FONTS.body, color: colors.textPrimary }}>
        {message}
      </Text>
      <Pressable
        onPress={onRetry}
        accessibilityRole="button"
        accessibilityLabel={t('common.retry')}
        hitSlop={8}
        style={{
          paddingHorizontal: SPACING.md,
          paddingVertical: SPACING.xs,
          borderRadius: RADIUS.full,
          backgroundColor: colors.surface,
        }}
      >
        <Text style={{ fontSize: 12, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
          {t('common.retry')}
        </Text>
      </Pressable>
    </View>
  )
}
